import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import {
  Box, Typography, Container, Grid, CircularProgress, Chip,
} from '@mui/material';
import PageBanner from '../components/PageBanner';
import SectionSidebar from '../components/SectionSidebar';
import { Link as RouterLink } from 'react-router-dom';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import EventIcon from '@mui/icons-material/Event';
import CampaignIcon from '@mui/icons-material/Campaign';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EmailIcon from '@mui/icons-material/Email';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import UpdateIcon from '@mui/icons-material/Update';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const GOLD = '#d4af37';

interface NewsOverviewContent {
  pageTitle: string;
  pageSubtitle: string;
  introText: string;
  newsHighlight: string;
  eventsHighlight: string;
  announcementsHighlight: string;
  subscriptionTitle: string;
  subscriptionText: string;
}

export default function NewsOverview() {
  const [email, setEmail] = useState('');
  const [content, setContent] = useState<NewsOverviewContent>({
    pageTitle: 'Stay Connected with Temple Updates',
    pageSubtitle: 'Get the latest news, events, and announcements from Muchukunnu Temple',
    introText: 'Welcome to our News & Events section. Stay informed about temple activities, upcoming festivals, and important announcements.',
    newsHighlight: 'Daily activities, priest schedules, and general news from the temple administration.',
    eventsHighlight: 'Festival calendars, special poojas, and community programs with dates and essential details.',
    announcementsHighlight: 'Official communications, circulars, and day-to-day notices for devotees and staff.',
    subscriptionTitle: 'Subscribe to Updates',
    subscriptionText: 'Never miss an update! Subscribe to receive notifications about important temple news, upcoming events, and special announcements directly to your email.',
  });
  const [loading, setLoading] = useState(true);
  const [recentNews, setRecentNews] = useState<any[]>([]);

  useEffect(() => {
    loadContent();
    loadRecentNews();
  }, []);

  const loadContent = async () => {
    try {
      const docRef = doc(db, 'news_overview_content', 'main');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setContent(docSnap.data() as NewsOverviewContent);
      }
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRecentNews = async () => {
    try {
      const articlesSnapshot = await getDocs(collection(db, 'news_section_articles'));
      const articles: any[] = [];
      articlesSnapshot.forEach((doc) => {
        articles.push({ id: doc.id, ...doc.data(), type: 'article' });
      });
      const announcementsSnapshot = await getDocs(collection(db, 'news_section_announcements'));
      const announcements: any[] = [];
      announcementsSnapshot.forEach((doc) => {
        announcements.push({ id: doc.id, ...doc.data(), type: 'announcement' });
      });
      const combined = [...articles, ...announcements]
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 5);
      setRecentNews(combined);
    } catch (error) {
      console.error('Error loading recent news:', error);
    }
  };

  const highlights = [
    { title: 'Temple News & Updates', detail: content.newsHighlight, to: '/news/news', icon: NewspaperIcon, gradient: ['#3b82f6', '#2563eb'] },
    { title: 'Upcoming Events', detail: content.eventsHighlight, to: '/news/upcoming', icon: EventIcon, gradient: ['#22c55e', '#16a34a'] },
    { title: 'Announcements & Notices', detail: content.announcementsHighlight, to: '/news/announcements', icon: CampaignIcon, gradient: ['#f59e0b', '#d97706'] },
  ];

  return (
    <Box sx={{ bgcolor: '#f8fafb', minHeight: '100vh' }}>
      <SEO
        title="News & Events — Kottayil Temple | വാർത്തകൾ"
        description="Latest news, events, festivals, and announcements from Kottayil Temple (Kotta-Kovilakam Kshethram), Muchukunnu, Kerala."
        path="/news"
        keywords="Kottayil temple news, Kottayil temple Muchukunnu news, temple events, festivals, announcements, Muchukunnu temple updates, Kottayil Shiva temple events, Kozhikode temple news, കൊട്ടയിൽ ക്ഷേത്രം വാർത്തകൾ, കൊട്ടയിൽ ക്ഷേത്രം മുച്ചുകുന്ന്, temple news Kerala"
      />
      <PageBanner title="News & Events" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box sx={{
              borderRadius: '28px', overflow: 'hidden', mb: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #134e5e 0%, #0891b2 40%, #67e8f9 100%)',
              position: 'relative',
            }}>
              <Box sx={{ position: 'absolute', top: -50, right: -50, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                  }}>
                    <NotificationsActiveIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', sm: '2rem' }, color: '#fff', lineHeight: 1.15, mb: 0.5, textShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
                      {content.pageTitle}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>
                      {content.pageSubtitle}
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: { xs: '0.88rem', sm: '0.95rem' }, color: 'rgba(255,255,255,0.55)', lineHeight: 1.9, maxWidth: 560, mb: 2.5 }}>
                  {content.introText}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Chip icon={<NewspaperIcon sx={{ fontSize: '16px !important', color: '#0891b2 !important' }} />} label="Latest Updates" sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#134e5e', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', '& .MuiChip-icon': { ml: '8px' } }} />
                  <Chip icon={<UpdateIcon sx={{ fontSize: '16px !important', color: '#67e8f9 !important' }} />} label="Real-time News" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.15)', '& .MuiChip-icon': { ml: '8px' } }} />
                </Box>
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: GOLD }} />
              </Box>
            ) : (
              <>
                {/* HIGHLIGHT CARDS */}
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                  {highlights.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Grid item xs={12} sm={4} key={item.title}>
                        <Box
                          component={RouterLink}
                          to={item.to}
                          sx={{
                            display: 'block', textDecoration: 'none',
                            bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                            border: '1px solid rgba(0,0,0,0.06)',
                            boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                            transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                            '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 16px 48px rgba(0,0,0,0.1)', '& .hArrow': { transform: 'translateX(4px)' } },
                          }}
                        >
                          {/* Gradient header */}
                          <Box sx={{ background: `linear-gradient(135deg, ${item.gradient[0]}, ${item.gradient[1]})`, px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ width: 42, height: 42, borderRadius: '14px', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid rgba(255,255,255,0.2)' }}>
                              <Icon sx={{ fontSize: 22, color: '#fff' }} />
                            </Box>
                            <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#fff' }}>
                              {item.title}
                            </Typography>
                          </Box>
                          <Box sx={{ px: 2.5, py: 2 }}>
                            <Typography sx={{ fontSize: '0.76rem', color: '#888', lineHeight: 1.8, mb: 1.5 }}>
                              {item.detail}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: item.gradient[0] }}>View Details</Typography>
                              <ArrowForwardIcon className="hArrow" sx={{ fontSize: 14, color: item.gradient[0], transition: 'transform 300ms ease' }} />
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>

                {/* RECENT + SUBSCRIBE ROW */}
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                  {/* Recent Highlights */}
                  <Grid item xs={12} md={7}>
                    <Box sx={{ bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(0,0,0,0.04)', height: '100%' }}>
                      <Box sx={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', px: 2.5, py: 1.8, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 36, height: 36, borderRadius: '12px', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid rgba(255,255,255,0.2)' }}>
                          <AutoAwesomeIcon sx={{ fontSize: 18, color: '#fff' }} />
                        </Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#fff' }}>Recent Highlights</Typography>
                      </Box>
                      <Box sx={{ p: 2.5 }}>
                        {recentNews.length === 0 ? (
                          <Typography sx={{ fontSize: '0.78rem', color: '#aaa', py: 2 }}>No recent updates available.</Typography>
                        ) : (
                          recentNews.map((item, i) => (
                            <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.2, borderBottom: i < recentNews.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: item.type === 'article' ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'linear-gradient(135deg, #f59e0b, #d97706)', flexShrink: 0 }} />
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#333' }}>{item.title}</Typography>
                                <Typography sx={{ fontSize: '0.65rem', color: '#bbb' }}>
                                  {new Date(item.date).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}
                                </Typography>
                              </Box>
                              <Box sx={{ px: 1, py: 0.3, borderRadius: '8px', background: item.type === 'article' ? 'linear-gradient(135deg, rgba(59,130,246,0.1), rgba(37,99,235,0.1))' : 'linear-gradient(135deg, rgba(245,158,11,0.1), rgba(217,119,6,0.1))' }}>
                                <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: item.type === 'article' ? '#3b82f6' : '#f59e0b', textTransform: 'uppercase' }}>
                                  {item.type === 'article' ? 'News' : 'Alert'}
                                </Typography>
                              </Box>
                            </Box>
                          ))
                        )}
                      </Box>
                    </Box>
                  </Grid>

                  {/* Subscribe */}
                  <Grid item xs={12} md={5}>
                    <Box sx={{ bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(0,0,0,0.04)', height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ background: 'linear-gradient(135deg, #ec4899, #db2777)', px: 2.5, py: 1.8, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 36, height: 36, borderRadius: '12px', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid rgba(255,255,255,0.2)' }}>
                          <EmailIcon sx={{ fontSize: 18, color: '#fff' }} />
                        </Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#fff' }}>{content.subscriptionTitle}</Typography>
                      </Box>
                      <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Typography sx={{ fontSize: '0.76rem', color: '#888', lineHeight: 1.8, mb: 2, flex: 1 }}>
                          {content.subscriptionText}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Box
                            component="input"
                            type="email"
                            placeholder="your@email.com"
                            value={email}
                            onChange={(e: any) => setEmail(e.target.value)}
                            sx={{
                              flex: 1, height: 42, border: '1.5px solid rgba(0,0,0,0.1)', borderRadius: '12px',
                              px: 1.5, fontSize: '0.78rem', outline: 'none', fontFamily: 'inherit',
                              '&:focus': { borderColor: '#ec4899', boxShadow: '0 0 0 3px rgba(236,72,153,0.1)' },
                            }}
                          />
                          <Box
                            onClick={() => alert('Subscribed!')}
                            sx={{
                              height: 42, px: 2.5, borderRadius: '12px', cursor: 'pointer',
                              background: 'linear-gradient(135deg, #ec4899, #db2777)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'all 300ms',
                              '&:hover': { transform: 'scale(1.05)', boxShadow: '0 6px 20px rgba(236,72,153,0.4)' },
                            }}
                          >
                            <Typography sx={{ fontWeight: 800, fontSize: '0.74rem', color: '#fff', whiteSpace: 'nowrap' }}>Subscribe</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                {/* HOW TO USE */}
                <Box sx={{ bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(0,0,0,0.04)', mb: 3 }}>
                  <Box sx={{ background: 'linear-gradient(135deg, #06b6d4, #0891b2)', px: 2.5, py: 1.8, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '12px', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid rgba(255,255,255,0.2)' }}>
                      <TipsAndUpdatesIcon sx={{ fontSize: 18, color: '#fff' }} />
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#fff' }}>How to Use This Section</Typography>
                  </Box>
                  <Box sx={{ p: 2.5 }}>
                    <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.8, fontSize: '0.78rem', color: '#777', lineHeight: 1.7 } }}>
                      <li>Check <strong style={{ color: '#333' }}>Announcements</strong> for official communication from temple management</li>
                      <li>Use <strong style={{ color: '#333' }}>Upcoming Events</strong> to plan visits and register for special programs</li>
                      <li>Browse <strong style={{ color: '#333' }}>Past Events</strong> and the gallery for photo highlights and reports</li>
                      <li>Subscribe to our newsletter to never miss an important update or festival</li>
                    </Box>
                  </Box>
                </Box>

                {/* DARK SLATE FOOTER */}
                <Box sx={{
                  background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
                  borderRadius: '24px', p: { xs: 3, sm: 3.5 },
                  position: 'relative', overflow: 'hidden',
                  border: '1px solid rgba(255,255,255,0.05)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                }}>
                  <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                  <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}12 0%, transparent 70%)` }} />

                  <Box sx={{ position: 'relative', zIndex: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 2.5 }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: `linear-gradient(135deg, ${GOLD}, #b8941f)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 6px 20px ${GOLD}30` }}>
                        <NotificationsActiveIcon sx={{ fontSize: 20, color: '#fff' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff' }}>Quick Links</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {[
                        { label: 'Announcements', to: '/news/announcements', gradient: ['#f59e0b', '#d97706'] },
                        { label: 'Upcoming Events', to: '/news/upcoming', gradient: ['#22c55e', '#16a34a'] },
                        { label: 'Temple News', to: '/news/news', gradient: ['#3b82f6', '#2563eb'] },
                        { label: 'Notices', to: '/news/notices', gradient: ['#a855f7', '#7c3aed'] },
                      ].map((btn) => (
                        <Box
                          key={btn.label}
                          component={RouterLink}
                          to={btn.to}
                          sx={{
                            textDecoration: 'none',
                            px: 2.5, py: 1, borderRadius: '14px',
                            background: `linear-gradient(135deg, ${btn.gradient[0]}15, ${btn.gradient[1]}15)`,
                            border: `1.5px solid ${btn.gradient[0]}30`,
                            transition: 'all 300ms',
                            '&:hover': { background: `linear-gradient(135deg, ${btn.gradient[0]}25, ${btn.gradient[1]}25)`, borderColor: btn.gradient[0], transform: 'translateY(-2px)' },
                          }}
                        >
                          <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: 'rgba(255,255,255,0.8)' }}>{btn.label}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </>
            )}

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="news" activePath="/news" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
